var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { ArtbookLocale, OverElement } from "@/atoms";
import { CONFIG } from "@/config";
import { useFrame } from "@react-three/fiber";
import { useAtomValue, useUpdateAtom } from "jotai/utils";
import { clamp } from "lodash";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { AdditiveBlending, Vector2 } from "three";
import { useTextureWithCORS } from "../utils/three-loader-hooks";
function getLocaleValue(values, locale) {
    return (values.find(function (pos) { return pos.locale === locale; }) ||
        values.find(function (pos) { return !pos.locale; }) ||
        values[0]).value;
}
function getInnerPosition(position, box) {
    var coord = { x: 0, y: 0 };
    if ("centerX" in position) {
        coord.x = box.width / 2 + position.centerX;
    }
    else if ("right" in position) {
        coord.x = box.width - position.right;
    }
    else {
        coord.x = position.left;
    }
    if ("centerY" in position) {
        coord.y = box.height / 2 + position.centerY;
    }
    else if ("bottom" in position) {
        coord.y = box.height - position.bottom;
    }
    else {
        coord.y = position.top;
    }
    return coord;
}
export function Summary(_a) {
    var goToPage = _a.goToPage;
    var locale = useAtomValue(ArtbookLocale);
    var _b = __read(useState(null), 2), overPage = _b[0], setOverPage = _b[1];
    var setOverElement = useUpdateAtom(OverElement);
    function setOutPage(page) {
        if (overPage === page) {
            setOverPage(null);
        }
    }
    useEffect(function () {
        return function () {
            setOverElement({ action: "reset" });
        };
    }, [setOverElement]);
    var _c = CONFIG.summary, buildItems = _c.items, circle = _c.circle, debug = _c.debug;
    var _d = __read(useState(buildItems), 2), items = _d[0], setItems = _d[1];
    useEffect(function () {
        if (!debug)
            return;
        var interval = setInterval(function () {
            setItems(buildItems());
        }, 1000);
        return function () {
            clearInterval(interval);
        };
    }, [buildItems, debug]);
    return (React.createElement("group", null, items.map(function (_a) {
        var position = _a.position, size = _a.size, circlePosition = _a.circlePosition, circleScale = _a.circleScale, target = _a.target;
        var localePos = getLocaleValue(position, locale);
        var localeSize = getLocaleValue(size, locale);
        var localeCirclePos = circlePosition && circlePosition.length > 0
            ? getLocaleValue(circlePosition, locale)
            : circle.position;
        var localeCircleScale = circleScale && circleScale.length > 0
            ? getLocaleValue(circleScale, locale)
            : [1, 1];
        var numberPos = getInnerPosition(localeCirclePos, localeSize);
        return (React.createElement("group", { key: target, position: [localePos.x, 0, localePos.y] },
            React.createElement(MenuCircle, { position: [numberPos.x, 0.47, numberPos.y], scale: [localeCircleScale[0], localeCircleScale[1], 1], rotation: [-Math.PI / 2, 0, 0], over: target === overPage }),
            React.createElement("mesh", { onPointerOver: function (e) {
                    // stopPropagation is required to apply this action only on the closest mesh
                    e.stopPropagation();
                    setOverPage(target);
                    setOverElement({ action: "over", element: e.eventObject });
                }, onPointerOut: function (e) {
                    setOutPage(target);
                    setOverElement({ action: "out", element: e.eventObject });
                }, onClick: function (e) {
                    // stopPropagation is required to apply this action only on the closest mesh
                    e.stopPropagation();
                    goToPage(target - 1);
                }, position: [localeSize.width / 2, 0, localeSize.height / 2], scale: [localeSize.width, 1, localeSize.height] },
                React.createElement("boxGeometry", null),
                React.createElement("meshBasicMaterial", { transparent: true, opacity: 0.5, color: [1, 0, 0], visible: debug }))));
    })));
}
function MenuCircle(_a) {
    var over = _a.over, globalScale = _a.scale, props = __rest(_a, ["over", "scale"]);
    var _b = __read(useState(Date.now()), 2), changeTime = _b[0], setChangeTime = _b[1];
    var _c = CONFIG.summary.circle, sprite = _c.sprite, animation = _c.animation, scale = _c.scale;
    var baseTexture = useTextureWithCORS(sprite.src);
    var img = baseTexture.image;
    if (!(img instanceof HTMLImageElement)) {
        throw new Error("must be an Image");
    }
    var width = animation.orientation == "horizontal"
        ? img.width / animation.frames
        : img.width;
    var height = animation.orientation == "vertical"
        ? img.height / animation.frames
        : img.height;
    var ratio = width / height;
    var overTexture = useMemo(function () {
        var tex = baseTexture.clone();
        tex.generateMipmaps = true;
        tex.needsUpdate = true;
        return tex;
    }, [baseTexture]);
    useLayoutEffect(function () {
        if (animation.orientation == "horizontal") {
            overTexture.repeat = new Vector2(1 / animation.frames, 1);
        }
        else {
            overTexture.repeat = new Vector2(1, 1 / animation.frames);
        }
    }, [overTexture, animation.frames, animation.orientation]);
    var mat = useRef();
    useLayoutEffect(function () {
        setChangeTime(Date.now());
        if (animation.orientation == "horizontal") {
            overTexture.offset.x = 0;
        }
        else {
            overTexture.offset.y = 1;
        }
        if (mat.current != null) {
            mat.current.visible = over;
        }
    }, [animation.orientation, over, overTexture.offset]);
    useFrame(function () {
        var dt = Date.now() - changeTime;
        var progress = over ? clamp(dt / animation.duration, 0, 1) : 0;
        var frame = Math.min(animation.frames - 1, Math.floor(progress * animation.frames));
        if (animation.orientation === "horizontal") {
            overTexture.offset.x = frame / animation.frames;
        }
        else {
            overTexture.offset.y = 1 - frame / animation.frames;
        }
    });
    return (React.createElement("mesh", __assign({}, props, { scale: [
            ratio * scale[0] * globalScale[0],
            scale[1] * globalScale[1],
            1 * globalScale[2],
        ] }),
        React.createElement("planeGeometry", null),
        React.createElement("meshBasicMaterial", { ref: mat, transparent: true, map: overTexture, blending: AdditiveBlending })));
}
